import { z } from 'zod';

import { FeeService } from '@/pages/Fleet/Businesses/models';
import { DriverApi, Driver } from '@/pages/Fleet/Drivers/models';
import { VehicleApi, Vehicle } from '@/pages/Fleet/Vehicles/models';
import { Company } from '@/pages/Fleet/Companies/models/Company';

export enum AssignmentType {
  ASSIGNMENT = 'A',
  TEMPLATE = 'T',
  CALENDAR = 'C',
}

export interface AssignmentApi {
  id: string;
  name: string;
  feeService: FeeService;
  driver?: DriverApi;
  companion?: DriverApi | null;
  vehicle: VehicleApi | null;
  profitOrLoss: number | null;
  profitOrLossAmount: number | null;
  profitOrLossDetails: number | null;
  isProfitAboveThreshold: number | null;
  time_planned: number | null;
  time_worked: number | null;
  work_ratio: number | null;
  punctuality_record: number | null;
  value_fee: number;
  value_amount: number;
  scheduledFrom: string;
  scheduledTo: string;
  startWork: string | null;
  endWork: string | null;
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  saturday?: boolean;
  sunday?: boolean;
  repeat_until?: string;
  companion_amount?: number;
  status: string;
  created_by?: string;
}

export interface Assignment {
  id: string;
  name: string;
  feeService: FeeService;
  driver?: Driver;
  companion?: Driver | null;
  vehicle: Vehicle | null;
  company?: Company | null;
  profit: {
    percentage: number | null;
    amount: number | null;
    details: number | null;
    isAboveThreshold: boolean | null;
  };
  metrics: {
    planned: number | null;
    worked: number | null;
    ratio: number | null;
    punctuality: number | null;
  };
  schedule: {
    from: string;
    to: string;
  };
  work: {
    start: string | null;
    end: string | null;
  };
  value_fee: number;
  value_amount: number;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
  repeat_until: string;
  companion_amount?: number;
  status: string;
  created_by?: string;
}

export interface NewAssignment {
  type: AssignmentType;
  feeServiceId: string;
  driverId?: string;
  companionId?: string | null;
  companyId?: string;
  scheduledFrom: Date;
  scheduledTo: Date;
  value_fee: number;
  value_amount?: number;
  companion_amount?: number;
}

export interface UpdateAssignment {
  type?: AssignmentType;
  feeServiceId?: string;
  driverId?: string;
  companionId?: string | null;
  companyId?: string;
  vehicleId?: string | null;
  scheduledFrom?: Date;
  scheduledTo?: Date;
  value_fee?: number;
  value_amount?: number;
  companion_amount?: number;
}

export const assignmentSchema = z
  .object({
    feeServiceId: z.string().uuid().optional(),
    driverId: z.string().uuid().nullable().optional(),
    companionId: z.string().uuid().nullable().optional().or(z.literal('')),
    companyId: z.string().uuid().nullable().optional(),
    vehicleId: z.string().uuid().nullable().optional(),
    date: z.date().optional(),
    startTime: z.date().optional(),
    endTime: z.date().optional(),
    value_fee: z.preprocess((val) => Number(val), z.number()).optional(),
    value_amount: z.preprocess((val) => Number(val), z.number()).optional(),
    companion_amount: z.preprocess((val) => Number(val), z.number()).optional(),
  })
  .refine(
    (data) => {
      if (data.startTime && data.endTime) {
        return data.startTime < data.endTime;
      }
      return true;
    },
    {
      message: 'La hora de inicio debe ser menor que la hora de fin',
      path: ['startTime'],
    }
  )
  .transform((data) => {
    if (data.date) {
      const startDate = new Date(data.date);
      const endDate = new Date(data.date);

      if (data.startTime && data.endTime) {
        const startHours = data.startTime.getHours();
        const startMinutes = data.startTime.getMinutes();
        const endHours = data.endTime.getHours();
        const endMinutes = data.endTime.getMinutes();

        startDate.setHours(startHours, startMinutes, 0, 0);
        endDate.setHours(endHours, endMinutes, 0, 0);
      } else {
        // Si no hay horas específicas, usar horarios por defecto
        startDate.setHours(8, 0, 0, 0); // 8:00 AM
        endDate.setHours(18, 0, 0, 0); // 6:00 PM
      }

      return {
        ...data,
        scheduledFrom: startDate,
        scheduledTo: endDate,
      };
    }
    return data;
  });

export type AssignmentSchema = z.infer<typeof assignmentSchema> & {
  scheduledFrom?: Date;
  scheduledTo?: Date;
};
